import consts from '@shared/consts';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';


/**
 * Constants
 */
const root = window.document.documentElement;
const availableCustomThemes = window.txConsts.customThemes.map((theme) => theme.name);
const customThemesClasses = window.txConsts.customThemes.map((theme) => `theme-${theme.name}`);
const defaultThemes = ['dark'];
const initialAtomValue = 'dark';


/**
 * Helpers
 */
const setThemeCookieValue = (value: string) => {
    document.cookie = `${consts.cookies.theme}=${value};path=/;SameSite=Lax;max-age=31536000;`;
}

const parseTheme = (themeName: string) => {
    if (themeName === 'dark') {
        return {
            isInvalid: false,
            isDefault: true,
            isDarkScheme: true,
            lightDarkClass: 'dark',
            customClass: undefined,
        }
    } else if (availableCustomThemes.includes(themeName)) {
        const customTheme = window.txConsts.customThemes.find((theme) => theme.name === themeName);
        if (customTheme) {
            const isDarkScheme = customTheme.isDark;
            return {
                isInvalid: false,
                isDefault: false,
                isDarkScheme,
                lightDarkClass: isDarkScheme ? 'dark' : 'light',
                customClass: `theme-${themeName}`,
            }
        }
    }

    console.warn(`Could not find theme'${themeName}', defaulting to dark.`);
    return {
        isInvalid: false,
        isDefault: false,
        isDarkScheme: true,
        lightDarkClass: 'dark',
        customClass: undefined,
    }
}


/**
 * Atom
 */
const themeAtom = atom(initialAtomValue);
const isDarkModeAtom = atom((get) => {
    const currTheme = get(themeAtom);
    return parseTheme(currTheme).isDarkScheme;
});

setThemeCookieValue(initialAtomValue);


/**
 * Theme changer
 */
const applyNewTheme = (oldTheme: string, newTheme: string) => {
    const forcedTheme = newTheme === 'dark' || availableCustomThemes.includes(newTheme)
        ? newTheme
        : 'dark';
    const { isDarkScheme, lightDarkClass, customClass } = parseTheme(forcedTheme);

    root.classList.remove(...defaultThemes, ...customThemesClasses, 'light');
    root.classList.add(lightDarkClass);
    if (customClass) {
        root.classList.add(customClass);
    }

    const iframeBody = (document.getElementById('legacyPageIframe') as HTMLObjectElement)?.contentDocument?.body;
    if (iframeBody) {
        iframeBody.classList.add('theme--dark');
    }

    setThemeCookieValue(forcedTheme);
    console.log(`Changed theme from '${oldTheme}' to '${forcedTheme}'.`);
}


/**
 * Hooks
 */
export const useTheme = () => {
    const [atomTheme, setAtomTheme] = useAtom(themeAtom);

    const setTheme = (newTheme: string) => {
        const forcedTheme = newTheme === 'dark' || availableCustomThemes.includes(newTheme)
            ? newTheme
            : 'dark';
        if (forcedTheme === atomTheme) return;
        applyNewTheme(atomTheme, forcedTheme);
        setAtomTheme(forcedTheme);
    }

    return {
        theme: atomTheme,
        setTheme,
    }
};

export const useToggleTheme = () => {
    return () => {};
}

export const useIsDarkMode = () => {
    return useAtomValue(isDarkModeAtom);
}

export const useThemedImage = (baseImageUrl?: string) => {
    if(typeof baseImageUrl !== 'string') return;
    return baseImageUrl.replace(/{theme}/g, 'dark');
}
